import type { Firestore } from "firebase/firestore";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

const MAX_BATCH_OPS = 400;

export type MergeDuplicatePlayersClientResult = {
  success: true;
  targetPlayerId: string;
  removedPlayerIds: string[];
  participationsRewritten: number;
  participationsDroppedAsDuplicate: number;
  archivesUpdated: number;
};

/**
 * Fusionne les fiches `players` partageant le même `licenceId` (appel admin uniquement —
 * les règles Firestore exigent le claim admin pour écrire).
 * Même logique que l’ancienne Cloud Function : priorité au tournoi `tournamentId`, puis tie-break ID.
 */
export async function mergeDuplicatePlayerLicenceGroup(
  db: Firestore,
  licenceId: string,
  tournamentId: string
): Promise<MergeDuplicatePlayersClientResult> {
  const trimmed = licenceId.trim();
  if (!trimmed) {
    throw new Error("Numéro de licence invalide.");
  }
  if (!tournamentId.trim()) {
    throw new Error("Tournoi courant requis pour la priorité de fusion.");
  }

  const playersCol = collection(db, "players");
  const playersSnap = await getDocs(
    query(playersCol, where("licenceId", "==", trimmed))
  );

  if (playersSnap.size < 2) {
    throw new Error(
      "Il faut au moins deux fiches avec ce numéro de licence pour fusionner."
    );
  }

  const docIds = playersSnap.docs.map((d) => d.id).sort((a, b) => a.localeCompare(b));

  const priorityPartCol = collection(
    db,
    "tournois",
    tournamentId,
    "participation"
  );
  const prioritySnap = await getDocs(priorityPartCol);
  const counts = new Map<string, number>();
  docIds.forEach((id) => counts.set(id, 0));
  prioritySnap.forEach((d) => {
    const pid = d.data().playersId as string | undefined;
    if (pid && counts.has(pid)) {
      counts.set(pid, (counts.get(pid) ?? 0) + 1);
    }
  });

  let targetId = docIds[0];
  let maxC = counts.get(targetId) ?? 0;
  for (const id of docIds) {
    const c = counts.get(id) ?? 0;
    if (c > maxC) {
      maxC = c;
      targetId = id;
    } else if (c === maxC && id.localeCompare(targetId) < 0) {
      targetId = id;
    }
  }

  const loserIds = docIds.filter((id) => id !== targetId);

  const targetRef = doc(db, "players", targetId);
  const targetSnapOne = await getDoc(targetRef);
  const targetData = { ...(targetSnapOne.data() ?? {}) } as Record<string, unknown>;

  for (const lid of loserIds) {
    const loserSnap = await getDoc(doc(db, "players", lid));
    const ld = (loserSnap.data() ?? {}) as Record<string, unknown>;
    if (!targetData.email && typeof ld.email === "string" && ld.email) {
      await updateDoc(targetRef, { email: ld.email });
      targetData.email = ld.email;
    }
  }

  let participationsRewritten = 0;
  let participationsDroppedAsDuplicate = 0;
  let archivesUpdated = 0;

  let batch = writeBatch(db);
  let batchOps = 0;

  const enqueueFlush = async (): Promise<void> => {
    if (batchOps >= MAX_BATCH_OPS) {
      await batch.commit();
      batch = writeBatch(db);
      batchOps = 0;
    }
  };

  const tournoisSnap = await getDocs(collection(db, "tournois"));

  for (const tDoc of tournoisSnap.docs) {
    const tid = tDoc.id;
    const partCol = collection(db, "tournois", tid, "participation");
    const tabCol = collection(db, "tournois", tid, "tableaux");

    const targetPartsSnap = await getDocs(
      query(partCol, where("playersId", "==", targetId))
    );
    const targetTableaux = new Set(
      targetPartsSnap.docs
        .map((d) => d.data().tableauId as string | undefined)
        .filter(Boolean) as string[]
    );

    for (const loserId of loserIds) {
      const loserPartsSnap = await getDocs(
        query(partCol, where("playersId", "==", loserId))
      );
      for (const pDoc of loserPartsSnap.docs) {
        const data = pDoc.data();
        const tableauId = data.tableauId as string | undefined;
        if (!tableauId) {
          continue;
        }

        if (targetTableaux.has(tableauId)) {
          const privCol = collection(pDoc.ref, "private");
          const privSnap = await getDocs(privCol);
          for (const pr of privSnap.docs) {
            batch.delete(pr.ref);
            batchOps++;
          }
          batch.delete(pDoc.ref);
          batchOps++;
          batch.update(doc(tabCol, tableauId), {
            nbParticipants: increment(-1),
          });
          batchOps++;
          participationsDroppedAsDuplicate++;
        } else {
          batch.update(pDoc.ref, { playersId: targetId });
          batchOps++;
          targetTableaux.add(tableauId);
          participationsRewritten++;
        }
        await enqueueFlush();
      }
    }

    const archCol = collection(db, "tournois", tid, "participation_archives");
    const archSnap = await getDocs(archCol);
    for (const aDoc of archSnap.docs) {
      const d = aDoc.data();
      const pid = d.playersId as string | undefined;
      if (pid && loserIds.includes(pid)) {
        batch.update(aDoc.ref, { playersId: targetId });
        batchOps++;
        archivesUpdated++;
        await enqueueFlush();
      }
    }
  }

  if (batchOps > 0) {
    await batch.commit();
    batch = writeBatch(db);
    batchOps = 0;
  }

  for (const lid of loserIds) {
    batch.delete(doc(db, "players", lid));
    batchOps++;
    await enqueueFlush();
  }
  if (batchOps > 0) {
    await batch.commit();
  }

  return {
    success: true,
    targetPlayerId: targetId,
    removedPlayerIds: loserIds,
    participationsRewritten,
    participationsDroppedAsDuplicate,
    archivesUpdated,
  };
}
